import axios from 'axios';
import { SELECTED_LANGUAGE, TOKEN } from 'constants/localStorageConstants';
import { refreshCognito } from 'services/aws-cognito';
import { checkTokenExpired } from 'utils/tokenUtils';

//This axios instance is used to pass authorization headers to all aircontrol endpoints
//and to manage the token and refresh token flow via interceptors
//External api calls should not use axiosInstance. They should use the plain axios package.
const axiosInstance = axios.create();

let tokenIsRefreshing = false;
let requestQueue = [];

const processQueue = (error, token = null) => {
	for (const prom of requestQueue) {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	}
	requestQueue = [];
};

axiosInstance.interceptors.request.use(
	async originalRequest => {
		let token = localStorage.getItem(TOKEN);
		if (token) {
			const tokenExpired = checkTokenExpired(token);

			if (tokenExpired) {
				//Token will expire in 5 min or less - initiate refresh flow
				if (tokenIsRefreshing) {
					// Refresh token is already being fetched
					return new Promise((resolve, reject) => {
						// Add request to queue as a new promise
						requestQueue.push({ resolve, reject });
					})
						.then(token => {
							//This promise is resolved after the new token is received. The new token is passed in.
							originalRequest.headers.Authorization = `Bearer ${token}`;
							return originalRequest;
						})
						.catch(error => {
							throw error;
						});
				} else {
					//Return a new promise
					return new Promise((resolve, reject) => {
						// Use refresh token to get new token
						tokenIsRefreshing = true;

						refreshCognito()
							.then(newTokens => {
								token = newTokens?.id_token;
								originalRequest.headers.Authorization = `Bearer ${token}`;
								tokenIsRefreshing = false;
								//Resolve the requests in the queue and pass new token
								processQueue(null, token);
								//Resolve the current request
								resolve(originalRequest);
							})
							.catch(error => {
								processQueue(error, null);
								reject(error);
							});
					});
				}
			}
			// Set authorization header if no refresh flow needed
			originalRequest.headers.Authorization = `Bearer ${token}`;
			const language = localStorage.getItem(SELECTED_LANGUAGE);
			originalRequest.headers['Accept-Language'] = language;
		}
		return originalRequest;
	},
	function (error) {
		return Promise.reject(error);
	}
);

// --- Login-window retry -------------------------------------------------------
// After a partner (SSO) login, briefly retry transient 401/403s so a first-login
// race (a request firing a beat before the token has fully settled) doesn't destroy
// a valid session via the global auth-error -> logout cascade. Scoped to a short
// window so established sessions keep today's immediate-logout behavior.
const LOGIN_RETRY_KEY = '__loginRetryUntil';
const LOGIN_RETRY_WINDOW_MS = 30000;
const LOGIN_RETRY_MAX = 2;
const LOGIN_RETRY_BASE_DELAY_MS = 700;

// Open the retry window; called by the partner-login flow once a valid token exists.
export const markPartnerLoginWindow = () => {
	try {
		localStorage.setItem(LOGIN_RETRY_KEY, String(Date.now() + LOGIN_RETRY_WINDOW_MS));
	} catch {
		/* ignore storage errors */
	}
};

const withinLoginWindow = () => {
	try {
		return Number(localStorage.getItem(LOGIN_RETRY_KEY) || 0) > Date.now();
	} catch {
		return false;
	}
};

axiosInstance.interceptors.response.use(
	response => response,
	async error => {
		const originalRequest = error.config;
		const status = error.response?.status;

		if (originalRequest && (status === 401 || status === 403) && withinLoginWindow()) {
			originalRequest.__loginRetryCount = originalRequest.__loginRetryCount || 0;
			if (originalRequest.__loginRetryCount < LOGIN_RETRY_MAX) {
				originalRequest.__loginRetryCount += 1;
				await new Promise(resolve =>
					setTimeout(resolve, LOGIN_RETRY_BASE_DELAY_MS * originalRequest.__loginRetryCount)
				);
				// Re-issue through the instance so the request interceptor re-attaches the token.
				return axiosInstance(originalRequest);
			}
		}

		return Promise.reject(error);
	}
);

export default axiosInstance;
